import { useEffect, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppDispatch } from '@/store/hooks';
import {
  authCheckComplete,
  logout as logoutAction,
  setCredentials,
  setError,
  setLoading,
  setToken,
  setUser,
  type AuthUser,
} from '@/store/slices/authSlice';
import { clearProfile } from '@/store/slices/userProfileSlice';
import { clearTenant } from '@/store/slices/tenantSlice';
import {
  type AuthActions,
  registerAuthActions,
  unregisterAuthActions,
} from '@/lib/auth/authActions';
import { AuthActionsProvider } from '@/lib/auth/AuthActionsContext';
import { requestInteractiveLogin } from '@/lib/auth/interactiveLogin';

interface Auth0SyncProviderProps {
  children: React.ReactNode;
}

function parseAuth0User(auth0User: Record<string, unknown> | undefined): AuthUser | null {
  if (!auth0User) return null;

  return {
    sub: (auth0User.sub as string) ?? '',
    email: ((auth0User.email as string) ?? '').toLowerCase(),
    name: (auth0User.name as string) ?? '',
    orgName: auth0User.org_name as string | undefined,
    tenantCode: auth0User.tenant_code as string | undefined,
  };
}

function requiresInteractiveLogin(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message.includes('missing refresh token') ||
    message.includes('invalid refresh token') ||
    message.includes('login_required')
  );
}

/**
 * A one-way compatibility bridge from Auth0 into Redux.
 *
 * Authentication decisions use the Auth0 SDK directly. Redux only receives a
 * copy of the current user/access token for existing application consumers and
 * RTK Query header injection.
 */
export function Auth0SyncProvider({ children }: Auth0SyncProviderProps) {
  const {
    isAuthenticated,
    isLoading,
    user: auth0User,
    error: auth0Error,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0();
  const dispatch = useAppDispatch();
  const syncedIdentityRef = useRef<string | null>(null);
  const syncInProgressRef = useRef(false);
  const wasAuthenticatedRef = useRef(false);

  const parsedUser = useMemo(
    () => parseAuth0User(auth0User as Record<string, unknown> | undefined),
    [auth0User],
  );

  useEffect(() => {
    dispatch(setLoading(isLoading));
  }, [isLoading, dispatch]);

  useEffect(() => {
    if (parsedUser) dispatch(setUser(parsedUser));
  }, [parsedUser, dispatch]);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated || !parsedUser) {
      if (wasAuthenticatedRef.current) {
        dispatch(logoutAction());
        dispatch(clearProfile());
        dispatch(clearTenant());
      } else {
        dispatch(authCheckComplete());
      }
      wasAuthenticatedRef.current = false;
      syncedIdentityRef.current = null;
      return;
    }

    wasAuthenticatedRef.current = true;
    const identityKey = `${parsedUser.sub}:${parsedUser.tenantCode ?? ''}`;
    if (syncedIdentityRef.current === identityKey || syncInProgressRef.current) return;

    syncInProgressRef.current = true;
    void getAccessTokenSilently()
      .then((token) => {
        dispatch(setCredentials({ token, user: parsedUser }));
        syncedIdentityRef.current = identityKey;
      })
      .catch((error: unknown) => {
        console.error('[Auth0SyncProvider] Access token acquisition failed:', error);
        if (requiresInteractiveLogin(error)) {
          requestInteractiveLogin({ reason: 'token-unavailable' });
        } else {
          dispatch(
            setError(error instanceof Error ? error.message : 'Access token acquisition failed'),
          );
        }
      })
      .finally(() => {
        syncInProgressRef.current = false;
      });
  }, [isAuthenticated, isLoading, parsedUser, getAccessTokenSilently, dispatch]);

  useEffect(() => {
    if (!auth0Error) return;
    console.error('[Auth0SyncProvider] Auth0 error:', auth0Error);
    dispatch(setError(auth0Error.message));
  }, [auth0Error, dispatch]);

  const authActions: AuthActions = useMemo(
    () => ({
      login: async (options) => {
        const authorizationParams = {
          ...(options?.organization ? { organization: options.organization } : undefined),
          ...(options?.tenantCode ? { tenant_code: options.tenantCode } : undefined),
          ...(options?.invitation ? { invitation: options.invitation } : undefined),
        };

        await loginWithRedirect({
          appState: { returnTo: options?.returnTo ?? window.location.pathname },
          ...(Object.keys(authorizationParams).length > 0
            ? { authorizationParams }
            : undefined),
        });
      },
      logout: async () => {
        dispatch(logoutAction());
        dispatch(clearProfile());
        dispatch(clearTenant());
        await auth0Logout({ logoutParams: { returnTo: window.location.origin } });
      },
      getAccessToken: async () => {
        try {
          const token = await getAccessTokenSilently({ cacheMode: 'off' });
          dispatch(setToken(token));
          return token;
        } catch {
          return null;
        }
      },
    }),
    [loginWithRedirect, auth0Logout, getAccessTokenSilently, dispatch],
  );

  useEffect(() => {
    registerAuthActions(authActions);
    return () => unregisterAuthActions();
  }, [authActions]);

  return <AuthActionsProvider value={authActions}>{children}</AuthActionsProvider>;
}
